import { useEffect, useState } from "react";
import Button from "./Button";
import { ArrowRight } from "./icons";
import { COMPANY } from "../data/company";
import "./get-app.css";

const CONSOLE_URL = `${COMPANY.urls.console}/login`;

/**
 * "android" | "ios" | "desktop".
 *
 * Starts as "desktop", which is also what the prerendered HTML and search
 * engines see: the computer layout names the web app and both stores, so a
 * crawler reads every way in. A phone swaps to its own store's badge after
 * load. iPadOS reports itself as a Mac, so a Mac with a touch screen counts as
 * iOS.
 */
export function detectPlatform(nav = typeof navigator === "undefined" ? null : navigator) {
  if (!nav) return "desktop";
  const ua = nav.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && nav.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

export function usePlatform() {
  const [platform, setPlatform] = useState("desktop");
  useEffect(() => setPlatform(detectPlatform()), []);
  return platform;
}

/**
 * The stores' own artwork, unaltered. Apple and Google both require their
 * badge files as published: no redrawing, no recolouring, no custom button
 * around their logo. `height` is the height of the black badge itself.
 *
 * Google's PNG carries about 24% clear space around the badge, so its image is
 * scaled up and pulled back in with negative margins until the badge matches
 * Apple's height (see get-app.css). Both are served from /badges/.
 */
export function StoreBadge({ store, height = 52 }) {
  const apple = store === "app-store";
  return (
    <a
      className={`store-badge store-badge--${store}`}
      href={apple ? COMPANY.urls.appStoreClub : COMPANY.urls.playClub}
      target="_blank"
      rel="noopener noreferrer"
      style={{ "--badge-h": `${height}px` }}
    >
      <img
        src={apple ? "/badges/app-store.svg" : "/badges/google-play.png"}
        alt={apple ? "Download on the App Store" : "Get it on Google Play"}
        width={apple ? 120 : 646}
        height={apple ? 40 : 250}
      />
    </a>
  );
}

/**
 * The main way in, drawn for whoever is looking.
 *
 * - A phone gets its own store's badge, with the browser one link away.
 * - A computer gets "Start free on the web" (nothing to install first), with
 *   both badges under it for someone who will install on a phone later.
 * - `short` is the navbar's and the floating bar's size: a phone still gets its
 *   badge (40px, the smallest comfortable size), a computer gets a small
 *   "Start on web".
 * - `webOnly` is for the navbar, a 56px bar that cannot hold a badge and its
 *   clear space on any device.
 */
export default function GetApp({ size = "lg", link = true, short = false, webOnly = false, className = "" }) {
  const platform = usePlatform();
  const phone = platform !== "desktop";
  const store = platform === "ios" ? "app-store" : "google-play";

  let body;
  if (webOnly || (short && !phone)) {
    body = (
      <Button href={CONSOLE_URL} variant="primary" size={size}>
        Start on web
      </Button>
    );
  } else if (short) {
    body = <StoreBadge store={store} height={40} />;
  } else if (phone) {
    body = (
      <>
        <StoreBadge store={store} />
        {link && (
          <a className="get-app__link" href={CONSOLE_URL} target="_blank" rel="noopener noreferrer">
            Or use it in your browser
            <ArrowRight />
          </a>
        )}
      </>
    );
  } else {
    body = (
      <>
        <Button href={CONSOLE_URL} variant="primary" size={size}>
          Start free on the web
          <span className="btn__glyph">
            <ArrowRight />
          </span>
        </Button>
        <div className="get-app__badges">
          <StoreBadge store="app-store" height={44} />
          <StoreBadge store="google-play" height={44} />
        </div>
      </>
    );
  }

  return (
    <div className={`get-app ${className}`.trim()} data-platform={platform}>
      {body}
    </div>
  );
}
